import { useState, useEffect } from "react";
import { Card, CardContent } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Area,
  AreaChart,
} from "recharts";
import { TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { getAnalyticsTrends, formatCurrency } from "../lib/api";
import { COMPANIES } from "../lib/companies";
import { toast } from "sonner";

const COLORS = {
  revenue: "#10b981",
  expenses: "#ef4444",
  fixed: "#f59e0b",
  net: "#3b82f6",
  navy: "#1E3A5F",
  orange: "#F5A623",
};

const COMPANY_CHART_COLORS = ["#1E3A5F", "#F5A623", "#10b981"];
const AGING_COLORS = ["#10b981", "#f59e0b", "#f97316", "#ef4444"];

const formatLabel = (month) => {
  if (!month) return "";
  const [y, m] = month.split("-");
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[parseInt(m, 10) - 1]} ${y.slice(2)}`;
};

const formatAxisValue = (val) => {
  if (val >= 10000000) return `${(val / 10000000).toFixed(1)}Cr`;
  if (val >= 100000) return `${(val / 100000).toFixed(1)}L`;
  if (val >= 1000) return `${(val / 1000).toFixed(0)}K`;
  return val;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3">
      <p className="text-sm font-semibold text-slate-700 mb-1">{formatLabel(label)}</p>
      {payload.map((entry, idx) => (
        <p key={`${entry.name}-${idx}`} className="text-xs" style={{ color: entry.color }}>
          {entry.name}: {formatCurrency(entry.value)}
        </p>
      ))}
    </div>
  );
};

const KpiCard = ({ title, value, icon: Icon, color, subtitle }) => (
  <div className="flex items-start gap-2 sm:gap-3">
    <div className={`p-1.5 sm:p-2 rounded-lg ${color}`}>
      <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
    </div>
    <div className="min-w-0">
      <p className="text-xs text-slate-500">{title}</p>
      <p className="text-sm sm:text-lg font-bold tabular-nums">{formatCurrency(value)}</p>
      {subtitle && <p className="text-xs text-slate-400 truncate">{subtitle}</p>}
    </div>
  </div>
);

export default function Analytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState("all");
  const [months, setMonths] = useState(12);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await getAnalyticsTrends(company, months);
        setData(res.data);
      } catch {
        toast.error("Failed to load analytics");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [company, months]);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="analytics-loading">
        <div className="animate-spin h-8 w-8 border-4 border-brand-orange border-t-transparent rounded-full" />
      </div>
    );
  }

  const trends = data.monthly_trends || [];
  const latestMonth = trends[trends.length - 1] || {};
  const prevMonth = trends[trends.length - 2] || {};
  const revChange = latestMonth.revenue - (prevMonth.revenue || 0);
  const expChange = latestMonth.total_outflow - (prevMonth.total_outflow || 0);

  const receivablesAging = data.receivables_aging || {};
  const payablesAging = data.payables_aging || {};
  const agingData = [
    { name: "Current", receivables: receivablesAging.current || 0, payables: payablesAging.current || 0 },
    { name: "1-30 Days", receivables: receivablesAging["30_days"] || 0, payables: payablesAging["30_days"] || 0 },
    { name: "31-60 Days", receivables: receivablesAging["60_days"] || 0, payables: payablesAging["60_days"] || 0 },
    { name: "90+ Days", receivables: receivablesAging["90_plus"] || 0, payables: payablesAging["90_plus"] || 0 },
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="analytics-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Trend Analysis</h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            Revenue, expenses, and financial health over time
          </p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <Select value={company} onValueChange={setCompany}>
            <SelectTrigger className="flex-1 sm:flex-none sm:w-44" data-testid="analytics-company-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {COMPANIES.map((c) => (
                <SelectItem key={c.value} value={c.value}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={months.toString()} onValueChange={(v) => setMonths(parseInt(v, 10))}>
            <SelectTrigger className="w-24 sm:w-28" data-testid="analytics-months-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="6">6 Mo</SelectItem>
              <SelectItem value="12">12 Mo</SelectItem>
              <SelectItem value="24">24 Mo</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* KPI Strip */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <KpiCard
              title="Revenue (Latest Month)"
              value={latestMonth.revenue || 0}
              icon={revChange >= 0 ? ArrowUpRight : ArrowDownRight}
              color={revChange >= 0 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}
              subtitle={`${revChange >= 0 ? "+" : ""}${formatCurrency(revChange)} vs prev month`}
            />
            <KpiCard
              title="Total Outflow (Latest)"
              value={latestMonth.total_outflow || 0}
              icon={expChange >= 0 ? TrendingUp : TrendingDown}
              color={expChange <= 0 ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}
              subtitle={`${expChange >= 0 ? "+" : ""}${formatCurrency(expChange)} vs prev month`}
            />
            <KpiCard
              title="Net Cash Flow (Latest)"
              value={latestMonth.net_flow || 0}
              icon={latestMonth.net_flow >= 0 ? TrendingUp : TrendingDown}
              color={latestMonth.net_flow >= 0 ? "bg-blue-100 text-blue-700" : "bg-red-100 text-red-700"}
            />
            <KpiCard
              title="Bank Balance"
              value={data.current_bank_balance || 0}
              icon={TrendingUp}
              color="bg-slate-100 text-slate-700"
            />
          </div>
        </CardContent>
      </Card>

      {/* Revenue vs Expenses */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-4">Revenue vs Total Outflow</h3>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={trends}>
                <defs>
                  <linearGradient id="gradRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.revenue} stopOpacity={0.2} />
                    <stop offset="95%" stopColor={COLORS.revenue} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradExpenses" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.expenses} stopOpacity={0.2} />
                    <stop offset="95%" stopColor={COLORS.expenses} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} tickFormatter={formatLabel} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={formatAxisValue} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area type="monotone" dataKey="revenue" name="Revenue" stroke={COLORS.revenue} fill="url(#gradRevenue)" strokeWidth={2} />
                <Area type="monotone" dataKey="total_outflow" name="Total Outflow" stroke={COLORS.expenses} fill="url(#gradExpenses)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Net Cash Flow */}
        <Card>
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-4">Monthly Net Cash Flow</h3>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={trends}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} tickFormatter={formatLabel} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={formatAxisValue} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="net_flow" name="Net Flow" radius={[4, 4, 0, 0]}>
                  {trends.map((entry, idx) => (
                    <Cell
                      key={`net-${entry.month}-${idx}`}
                      fill={entry.net_flow >= 0 ? COLORS.revenue : COLORS.expenses}
                      opacity={0.85}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Company Revenue + Burn Rate */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-4">Company-wise Revenue</h3>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.company_revenue || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} tickFormatter={formatLabel} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={formatAxisValue} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Empact Studio" stackId="a" fill={COMPANY_CHART_COLORS[0]} radius={[0, 0, 0, 0]} />
                <Bar dataKey="Event Resource" stackId="a" fill={COMPANY_CHART_COLORS[1]} radius={[0, 0, 0, 0]} />
                <Bar dataKey="Vijay Sabhlok" stackId="a" fill={COMPANY_CHART_COLORS[2]} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Expense Breakdown */}
        <Card>
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-4">Monthly Burn Rate</h3>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={trends}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} tickFormatter={formatLabel} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={formatAxisValue} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="expenses" name="Variable Expenses" stroke={COLORS.expenses} strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="fixed_expenses" name="Fixed Expenses" stroke={COLORS.fixed} strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="total_outflow" name="Total Burn" stroke={COLORS.navy} strokeWidth={2.5} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Receivables & Payables Aging */}
      <Card>
        <CardContent className="p-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Receivables & Payables Aging</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <p className="text-xs text-slate-500 mb-2 text-center">Receivables by Age</p>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={agingData.filter((d) => d.receivables > 0)}
                    dataKey="receivables"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ name, value }) => `${name}: ${formatCurrency(value)}`}
                    labelLine={{ stroke: "#94a3b8" }}
                  >
                    {agingData.map((_, idx) => (
                      <Cell key={`pie-aging-${idx}`} fill={AGING_COLORS[idx]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(val) => formatCurrency(val)} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-2 text-center">Payables by Age</p>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={agingData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={formatAxisValue} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={80} />
                  <Tooltip formatter={(val) => formatCurrency(val)} />
                  <Bar dataKey="payables" name="Payables" radius={[0, 4, 4, 0]}>
                    {agingData.map((_, idx) => (
                      <Cell key={`bar-aging-${idx}`} fill={AGING_COLORS[idx]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
